import React, {useState, useRef} from 'react';
import PropTypes from 'prop-types';
import {Button, Heading, Text, LoadingSpinner, Card, Icon, WithTooltip} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {Link as RouterLink} from 'react-router';
import {Link as ScrollLink, scroller} from 'react-scroll';
import {isEmail} from 'validator';
import sessionStore from 'client/Session/SessionStore';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {getModelForResourceType} from 'resources/modelRegistry';
import classnames from 'classnames';

import RefreshTeacherList from './BulkReject/RefreshTeacherList.react';
import AddTeachers from './BulkReject/AddTeachers.react';
import EmailField from './BulkReject/EmailField.react';

import './update-teachers.scss';

//  This regular expression is looking for any line feed characters (\n), carriage return characters (\r),
//  any semicolons, or commas in the email addresses. The 'gi' modifier means the regular expression is doing
//  a full text search for \n, \r, ;, and , ignoring case.

const delimiter = new RegExp(/[\n\r;, ]/, 'gi');
const scrollOffset = -280;

const UpdateTeachers = ({params, schoolTeachers, pendingStaff, nonTeachers, rejectedTeachers}) => {
  const [showEmailField, setShowEmailField] = useState(false);
  const [showEmailTable, setShowEmailTable] = useState(false);
  const [emailField, setEmailField] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [emailMatches, setEmailMatches] = useState([]);
  const [newEmails, setNewEmails] = useState([]);
  const [activeCardRef, setActiveCardRef] = useState(null);
  const [activeTab, setActiveTab] = useState('teacher-list');
  const [missingEmails, setMissingEmails] = useState([]);
  const [activeOption, setActiveOption] = useState(null);

  const addTeachersRef = useRef();
  const refreshTeacherListRef = useRef();

  const filteredSchoolTeachers = schoolTeachers
    .getSchoolPersonnel()
    .filter((entry) => entry.getTeacher().size !== 0);
  const schoolTeacherEmails = filteredSchoolTeachers.map((entry) => {
    return entry.getTeacher().getEmail();
  });
  const pendingInviteEmails = pendingStaff.getSchoolPersonnelInvitations().map((invitation) => {
    return invitation.getEmail();
  });

  const nonTeacherEmails = nonTeachers.getSchoolPersonnel().map((entry) => {
    // User most likely got changed to student account so changing their email to empty string placeholders
    if (entry.getTeacher().size === 0) {
      return '';
    }
    return entry.getTeacher().getEmail();
  });

  // Removes duplicates in both arrays and combines them together
  const teacherPendingApprovedEmails = [
    ...new Set([...schoolTeacherEmails, ...pendingInviteEmails])
  ];
  const allPendingApprovedEmails = [
    ...new Set([...teacherPendingApprovedEmails, ...nonTeacherEmails])
  ];

  const clearEmailData = () => {
    setEmailField('');
    setShowEmailTable(false);
    setEmailError(null);
    setNewEmails([]);
    setEmailMatches([]);
    setMissingEmails([]);
  };

  const handleChange = (e) => {
    setEmailField(e.target.value);
    setEmailError(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleAddTeachers = () => {
    setShowEmailField(true);
    setActiveCardRef(addTeachersRef);
    setActiveOption('addTeachers');
    if (activeCardRef !== addTeachersRef) {
      clearEmailData();
    }
  };

  const handleRefreshTeacherList = () => {
    setShowEmailField(true);
    setActiveCardRef(refreshTeacherListRef);
    setActiveOption('refreshTeachers');
    if (activeCardRef !== refreshTeacherListRef) {
      clearEmailData();
    }
  };

  // Handle email validation, email sort (determining which ones are new), and showing email address table
  const handleSubmit = () => {
    const input = emailField
      .trim()
      .split(delimiter)
      .map((email) => email.toLowerCase());
    const parsedInput = input
      .filter((item) => item) // filter out empty strings
      .reduce(
        (acc, curr) => {
          if (!isEmail(curr)) {
            acc.invalidItems.push({
              email: curr,
              reason:
                'One or more of your email addresses above were not formatted properly. Remove or correct the email addresses.'
            });
            return acc;
          }

          acc.validItems.push(curr);
          return acc;
        },
        {
          invalidItems: [],
          validItems: []
        }
      );
    if (parsedInput.validItems.length) {
      setShowEmailTable(true);
    }

    const hasError = !!parsedInput.invalidItems.length;
    setEmailError(hasError ? parsedInput.invalidItems[0].reason : null);
    setEmailField(parsedInput.invalidItems.map((item) => item.email).join(','));

    // These two variables below are emails that are being typed in the field at this instance.
    const submitNewEmails = parsedInput.validItems.filter(
      (email) => !allPendingApprovedEmails.includes(email)
    );
    const submitEmailMatches = parsedInput.validItems.filter((email) =>
      teacherPendingApprovedEmails.includes(email)
    );

    // These variables refer to when the user wants to go back to the field and add more emails after some have been submitted.
    // submitNewEmails will be emails added in future instances and newEmails will have ones from the first instance.
    // In addition, allNewEmails combines and spreads both arrays and makes sure there are no duplicates.

    const allNewEmails = [...new Set([...submitNewEmails, ...newEmails])];
    const allEmailMatches = [...new Set([...submitEmailMatches, ...emailMatches])];

    // setNewEmails and setEmailMatches are constantly being updated to show the compounded email table if the user wants to add more emails after submitting.
    setNewEmails(allNewEmails);
    setEmailMatches(allEmailMatches);
    const submitMissingEmails = teacherPendingApprovedEmails.filter(
      (email) => ![...allNewEmails, ...allEmailMatches].includes(email)
    );

    // setMissingEmails is updated every time the user inputs new emails to show the updated list of emails to reject.
    setMissingEmails(submitMissingEmails);

    if (!hasError) {
      scroller.scrollTo(activeCardRef === refreshTeacherListRef ? 'refreshteacher' : 'addteacher', {
        smooth: true,
        offset: scrollOffset,
        duration: 400
      });
    }
  };

  return (
    <div className='update-teachers u-position_relative'>
      <Button
        variant='text'
        size='s'
        as={RouterLink}
        to={`/school-admin/${params.schoolId}/manage-staff`}
        className='update-teachers__cancel-button u-mar-b_2'
      >
        <Icon icon='arrow-left' className='u-mar-r_1' />
        Cancel
      </Button>
      <Heading as='h1' className='u-mar-b_8 u-text-align_left' size='xl'>
        Update Teacher List
      </Heading>
      <Text emphasis='medium' size='m'>
        Choose an option
      </Text>
      <div className='update-teachers__option-card-container u-mar-t_2 u-pad-l_0'>
        <ScrollLink
          activeClass='active'
          to='emailfield'
          spy
          smooth
          offset={scrollOffset}
          duration={400}
        >
          <Card
            ref={refreshTeacherListRef}
            onClick={handleRefreshTeacherList}
            className={classnames('update-teachers__option-cards u-pad_4', {
              'update-teachers_option-cards--active': activeOption === 'refreshTeachers'
            })}
          >
            <div className='u-display_flex u-justify-items_space-between u-align-items_flex-start u-width_100pc'>
              <Heading
                className='update-teachers__option-card-title u-mar-b_1 u-text-align_left u-flex-grow_1'
                size='s'
                emphasis='high'
              >
                Add &amp; Remove Teachers
              </Heading>
              <div className='update-teachers__option-card-radio u-border-radius_circle' />
            </div>
            <Text
              className='update-teachers__option-card-description u-mar-b_1 u-text-align_left'
              emphasis='medium'
              size='m'
            >
              Add your entire teacher list. We&apos;ll automatically sort out who is new and who
              will be removed for you.
            </Text>
            <WithTooltip
              content={
                <div>
                  <text>
                    Only teacher users will be added or removed in this process. This process will
                    not affect administrator permissions. If you need to grant or remove
                    administrator permissions from one or more staff members, please reach out to us
                    at{' '}
                    <a
                      className='u-color_blue-300'
                      href='mailto:schools@albert.io?subject=Request to create an admin user'
                    >
                      schools@albert.io
                    </a>
                  </text>
                </div>
              }
              placement='right'
            >
              <Text
                as='div'
                emphasis='medium'
                size='s'
                italic
                underlined
                className='update-teachers__option-card-tooltip u-mar-b_1 u-text-align_left'
              >
                What about admins?
              </Text>
            </WithTooltip>
          </Card>
        </ScrollLink>
        <ScrollLink
          activeClass='active'
          to='emailfield'
          spy
          smooth
          offset={scrollOffset}
          duration={400}
        >
          <Card
            ref={addTeachersRef}
            onClick={handleAddTeachers}
            className={classnames('update-teachers__option-cards u-pad_4', {
              'update-teachers_option-cards--active': activeOption === 'addTeachers'
            })}
          >
            <div className='u-display_flex u-justify-items_space-between u-align-items_flex-start u-width_100pc'>
              <Heading
                className='update-teachers__option-card-title u-mar-b_1 u-text-align_left u-flex-grow_1'
                size='s'
                emphasis='high'
              >
                Add Teachers
              </Heading>
              <div className='update-teachers__option-card-radio u-border-radius_circle' />
            </div>
            <Text
              className='update-teachers__option-card-description u-mar-b_1 u-mar-r_4 u-text-align_left'
              emphasis='medium'
              size='m'
            >
              Add one or multiple teachers to your school.
            </Text>
          </Card>
        </ScrollLink>
      </div>
      <Card
        id='emailfield'
        paddingLevel={3}
        shadow='far'
        className={classnames('update-teachers__email-field u-mar-t_6 u-mar-b_6', {
          'u-visibility_hidden': !showEmailField
        })}
      >
        {showEmailField && (
          <EmailField
            activeCardRef={activeCardRef}
            emailError={emailError}
            emailField={emailField}
            handleChange={handleChange}
            handleKeyPress={handleKeyPress}
            handleSubmit={handleSubmit}
            refreshTeacherListRef={refreshTeacherListRef}
            showEmailTable={showEmailTable}
          />
        )}
      </Card>
      {activeCardRef === refreshTeacherListRef ? (
        <div id='refreshteacher' className='u-mar-t_6'>
          {showEmailTable && (
            <RefreshTeacherList
              emailMatches={emailMatches}
              newEmails={newEmails}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              missingEmails={missingEmails}
              schoolTeachers={schoolTeachers}
              pendingStaff={pendingStaff}
              params={params}
              key={`${emailMatches.join('')}${newEmails.join('')}${missingEmails.join('')}`}
              rejectedTeachers={rejectedTeachers}
            />
          )}
        </div>
      ) : (
        <div id='addteacher' className='u-mar-b_6'>
          {showEmailTable && (
            <AddTeachers
              emailMatches={emailMatches}
              newEmails={newEmails}
              params={params}
              key={`${emailMatches.join('')}${newEmails.join('')}${missingEmails.join('')}`}
              rejectedTeachers={rejectedTeachers}
            />
          )}
        </div>
      )}
    </div>
  );
};

UpdateTeachers.propTypes = {
  params: PropTypes.object,
  schoolTeachers: PropTypes.instanceOf(getModelForResourceType('school_v5')),
  pendingStaff: PropTypes.instanceOf(getModelForResourceType('school_v5')),
  nonTeachers: PropTypes.instanceOf(getModelForResourceType('school_v5')),
  rejectedTeachers: PropTypes.instanceOf(getModelForResourceType('school_v5'))
};

export default awaitMandarkQueries(
  (props) => ({
    loadingElement: <LoadingSpinner className='u-mar-tb_4' size={2} />,
    queries: {
      schoolTeachers: resource('school_v5')
        .mandarkEndpoint(['schools_v5', props.params.schoolId])
        .include('email_domains_v1')
        .include(
          'school_personnel_invitations_v1,school_personnel_v1.teacher_v1,school_personnel_v1.role_v1'
        )
        .filter({
          included: {
            school_personnel_v1: {
              status: {not: 'rejected'},
              role_v1: {name: 'teacher'}
            }
          }
        }),
      adminUser: resource('user_v2').mandarkEndpoint(['users_v2', sessionStore.getUserId()]),
      pendingStaff: resource('schools_v5')
        .mandarkEndpoint(['schools_v5', props.params.schoolId])
        .include('school_personnel_invitations_v1')
        .filter({
          included: {
            school_personnel_invitations_v1: {
              accepted_at: {
                null: true
              }
            }
          }
        }),
      nonTeachers: resource('school_v5')
        .mandarkEndpoint(['schools_v5', props.params.schoolId])
        .include('email_domains_v1')
        .include(
          'school_personnel_invitations_v1,school_personnel_v1.teacher_v1,school_personnel_v1.role_v1'
        )
        .filter({
          included: {
            school_personnel_v1: {
              role_v1: {
                name: {
                  not: 'teacher'
                }
              }
            }
          }
        }),
      rejectedTeachers: resource('school_v5')
        .mandarkEndpoint(['schools_v5', props.params.schoolId])
        .include('email_domains_v1')
        .include(
          'school_personnel_invitations_v1,school_personnel_v1.teacher_v1,school_personnel_v1.role_v1'
        )
        .filter({
          included: {
            school_personnel_v1: {
              status: 'rejected',
              role_v1: {name: 'teacher'}
            }
          }
        })
    }
  }),
  UpdateTeachers
);
